import { useEffect, useRef, useState } from 'react';
import DateRangeFilter from './dateRangeFilter/DateRangeFilter';
import { useFilterContext, useDerivedNetworkContext } from '@/context';
import ShowsFilter from './showsFilter/ShowsFilter';
import NetworksFilter from './networksFilter/NetworksFilter';
import EpisodesFilter, { type EpisodeFilter } from './episodesFilter/EpisodesFilter';
import { getShowParam, mixpanelTrack, RevenueDashboardWidget } from '@/utils';
import { MIXPANEL_EVENT } from '@/utils/mixpanelUtils/events';
import PlatformFilter from './platformFilter/PlatformFilter';
import {
  getDateAppliedEventByPageType,
  getDatePickerOpenedEventByPageType,
  getDatePresetSelectedEventByPageType,
  getShowsFilterOpenedEventByPageType,
  getShowsFilterSelectedEventByPageType,
} from '@/utils/mixpanelUtils/pageTypeHelpers';
import { usePageMetadata, useFilterNavigation } from '@/hooks';
import useNetworks from '@/hooks/useNetworks';

export enum VisibleFilterKey {
  Networks = 'networks',
  Shows = 'shows',
  Episodes = 'episodes',
  Platform = 'platform',
  DateRange = 'dateRange',
}

type FiltersProps = {
  visibleFilters: VisibleFilterKey[];
  onFilterApplied?: () => void;
};

/**
 * This component is used to render the filters.
 * It contains the date range filter and other filters based on visibleFilters.
 * @param {VisibleFilterKey[]} visibleFilters - Which filters to show.
 * @returns {React.JSX.Element} - The filters.
 */
const Filters = ({ visibleFilters, onFilterApplied }: FiltersProps) => {
  const { page: pageType } = usePageMetadata();
  const { filters, setFilters } = useFilterContext();
  const { selectedNetwork, shows } = useDerivedNetworkContext();
  const { goToNetwork, goToShow, goToDateRange } = useFilterNavigation();
  const { data: networks } = useNetworks();
  const noOfShows = shows.length;
  const prevNoOfShowsRef = useRef(noOfShows);

  // Local state for episode filter (mock data for now)
  const [selectedEpisode, setSelectedEpisode] = useState<EpisodeFilter>('all');

  if (!pageType) throw new Error('Page type is required');

  const isAudiencePage = pageType === 'audience';
  const isContentPerformancePage = pageType === 'content-performance';

  const showNetworks = visibleFilters.includes(VisibleFilterKey.Networks);
  const showShows = visibleFilters.includes(VisibleFilterKey.Shows) && shows.length > 1;
  const showEpisodes = visibleFilters.includes(VisibleFilterKey.Episodes);
  const showPlatform = visibleFilters.includes(VisibleFilterKey.Platform);
  const showDateRange = visibleFilters.includes(VisibleFilterKey.DateRange);

  // Reset selected show to 'all' when shows become unavailable for selected date range, all is not available on audience page
  useEffect(() => {
    if (noOfShows === 0 && prevNoOfShowsRef.current > 0 && isAudiencePage) {
      setFilters((prev) => ({
        ...prev,
        selectedShows: [],
      }));
    }
    prevNoOfShowsRef.current = noOfShows;
  }, [noOfShows, setFilters, isAudiencePage]);

  return (
    <div className="flex flex-col lg:flex-row lg:grow gap-4 p-6">
      {showNetworks && (
        <div className={'w-full lg:flex-1 lg:min-w-0'}>
          <NetworksFilter
            networks={networks ?? []}
            selectedNetwork={selectedNetwork}
            onOpen={() => {
              mixpanelTrack(MIXPANEL_EVENT.networkSelect.opened, {
                widget: RevenueDashboardWidget.NetworkSelect,
                page: pageType,
              });
            }}
            onApply={(networkId) => {
              const network = networks?.find((n) => n.id === networkId);
              const networkName = network?.name ?? networkId;
              mixpanelTrack(MIXPANEL_EVENT.networkSelect.selected, {
                widget: RevenueDashboardWidget.NetworkSelect,
                page: pageType,
                networkName,
              });

              goToNetwork(networkId);
              onFilterApplied?.();
            }}
          />
        </div>
      )}

      {showShows && (
        <div className={'w-full lg:flex-1 lg:min-w-0'}>
          <ShowsFilter
            renderAllShows={!isAudiencePage && !isContentPerformancePage}
            shows={shows}
            selectedShow={filters.selectedShows[0] ?? 'all'}
            onOpen={() => {
              mixpanelTrack(getShowsFilterOpenedEventByPageType(pageType), {
                widget: RevenueDashboardWidget.ShowSelect,
                page: pageType,
              });
            }}
            onApply={(newSelectedShow) => {
              mixpanelTrack(getShowsFilterSelectedEventByPageType(pageType), {
                widget: RevenueDashboardWidget.ShowSelect,
                page: pageType,
                showId: getShowParam(newSelectedShow),
                showName: getShowParam(newSelectedShow),
              });

              goToShow(getShowParam(newSelectedShow));
              onFilterApplied?.();
            }}
          />
        </div>
      )}

      {showEpisodes && (
        <div className={'w-full lg:flex-1 lg:min-w-0'}>
          <EpisodesFilter
            selectedEpisode={selectedEpisode}
            onOpen={() => {
              mixpanelTrack(MIXPANEL_EVENT.earnings.episodesFilter.opened, {
                widget: RevenueDashboardWidget.EpisodeSelect,
                page: pageType,
              });
            }}
            onApply={(newSelectedEpisode) => {
              setSelectedEpisode(newSelectedEpisode);
              mixpanelTrack(MIXPANEL_EVENT.earnings.episodesFilter.selected, {
                widget: RevenueDashboardWidget.EpisodeSelect,
                page: pageType,
                episodeId: newSelectedEpisode === 'all' ? 'all' : newSelectedEpisode?.id,
              });
              onFilterApplied?.();
            }}
          />
        </div>
      )}

      {showPlatform && (
        <div className={'w-full lg:flex-1 lg:min-w-0'}>
          <PlatformFilter
            onOpen={() => {
              mixpanelTrack(MIXPANEL_EVENT.earnings.platformFilter.opened, {
                widget: RevenueDashboardWidget.PlatformSelect,
                page: pageType,
              });
            }}
            onApply={(newSelectedPlatforms) => {
              setFilters({
                ...filters,
                selectedPlatforms: newSelectedPlatforms,
              });

              mixpanelTrack(MIXPANEL_EVENT.earnings.platformFilter.selected, {
                widget: RevenueDashboardWidget.PlatformSelect,
                page: pageType,
                platforms:
                  newSelectedPlatforms.length > 0
                    ? newSelectedPlatforms.map((p) => p.label).join(', ')
                    : 'all',
              });
              onFilterApplied?.();
            }}
          />
        </div>
      )}

      {showDateRange && (
        <div className={'w-full lg:flex-1 lg:min-w-0'}>
          <DateRangeFilter
            dateRange={filters.dateRange}
            onOpen={() => {
              mixpanelTrack(getDatePickerOpenedEventByPageType(pageType), {
                widget: RevenueDashboardWidget.DateSelect,
                page: pageType,
              });
            }}
            onPresetSelect={(preset) => {
              mixpanelTrack(getDatePresetSelectedEventByPageType(pageType), {
                preset,
                widget: RevenueDashboardWidget.DateSelect,
                page: pageType,
              });
            }}
            onApply={(start, end) => {
              mixpanelTrack(getDateAppliedEventByPageType(pageType), {
                start,
                end,
                widget: RevenueDashboardWidget.DateSelect,
                page: pageType,
              });
              goToDateRange(start, end || start);
              onFilterApplied?.();
            }}
          />
        </div>
      )}
    </div>
  );
};

export default Filters;
